import cx from 'clsx';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import type { ClientDetails, MerchantDetails } from '../../types';
import { usePdfPreviewContext } from '../../pdf-preview-context.tsx';
import { Address } from './address';
import styles from './addresses.module.css';

interface AddressesProps {
  client?: ClientDetails;
  merchant: MerchantDetails;
}

export function Addresses({ client, merchant }: AddressesProps): ReactNode {
  const { formatMessage } = useIntl();
  const { displayLanguage, variant } = usePdfPreviewContext();

  const deliveryAddressLabel =
    client?.deliveryAddress?.addressName ??
    formatMessage({ id: 'pdf-preview.delivery-address' }, { language: displayLanguage });

  if (variant === 'DE') {
    if (!client) {
      return null;
    }

    return (
      <div className={cx(styles.addresses, styles.DE)}>
        {client.billingAddress ? (
          <Address
            address={client.billingAddress}
            addressType="client"
            data-testid="pdf-preview-addresses-billing-address"
            label={<h2 className="mb-8 label-2">{client.name}</h2>}
          />
        ) : null}
        {client.deliveryAddress ? (
          <Address
            address={client.deliveryAddress}
            addressType="client"
            data-testid="pdf-preview-addresses-delivery-address"
            label={<h2 className="body-2 mb-4">{deliveryAddressLabel}</h2>}
          />
        ) : null}
      </div>
    );
  }

  const merchantVatNumber = merchant.vatNumber
    ? `${formatMessage({ id: 'pdf-preview.vat-number' }, { language: displayLanguage })} ${merchant.vatNumber}`
    : undefined;
  const clientVatNumber = client?.vatNumber
    ? `${formatMessage({ id: 'pdf-preview.vat-number' }, { language: displayLanguage })} ${client.vatNumber}`
    : undefined;

  return (
    <div className={styles.addresses}>
      <Address
        address={merchant.address}
        addressType="merchant"
        data-testid="pdf-preview-addresses-merchant"
        label={<h2 className="body-1-bold mb-8">{merchant.legalName}</h2>}
      >
        {merchant.contactEmail ? (
          <p className="body-2" data-testid="pdf-preview-addresses-email">
            {merchant.contactEmail}
          </p>
        ) : null}
        {merchant.legalNumber ? (
          <p className="body-2" data-testid="pdf-preview-addresses-legal-number">
            {merchant.legalNumber}
          </p>
        ) : null}
        {merchantVatNumber ? (
          <p className="body-2" data-testid="pdf-preview-addresses-vat-number">
            {merchantVatNumber}
          </p>
        ) : null}
      </Address>
      {client?.billingAddress ? (
        <Address
          address={client.billingAddress}
          addressType="client"
          data-testid="pdf-preview-addresses-billing-address"
          label={<h2 className="body-1-bold mb-8">{client.name}</h2>}
        >
          {client.email ? (
            <p className="body-2" data-testid="pdf-preview-addresses-email">
              {client.email}
            </p>
          ) : null}
          {client.phoneNumber?.number ? (
            <p className="body-2" data-testid="pdf-preview-addresses-phone-number">
              {client.phoneNumber.number}
            </p>
          ) : null}
          {client.tinNumber ? (
            <p className="body-2" data-testid="pdf-preview-addresses-tin-number">
              {client.tinNumber}
            </p>
          ) : null}
          {clientVatNumber ? (
            <p className="body-2" data-testid="pdf-preview-addresses-vat-number">
              {clientVatNumber}
            </p>
          ) : null}
        </Address>
      ) : null}
      {client?.deliveryAddress ? (
        <Address
          address={client.deliveryAddress}
          addressType="client"
          data-testid="pdf-preview-addresses-delivery-address"
          label={<h2 className="body-1-bold mb-8">{deliveryAddressLabel}</h2>}
        />
      ) : null}
    </div>
  );
}
